import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Category, Currency, LedgerAccount, PlanGroupView, PlanItem } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { money, payeeName } from "../../shared/lib/format";
import { Modal } from "../../shared/ui/Modal";
import { RowMenu } from "../../shared/ui/RowMenu";
import { Select } from "../../shared/ui/Select";
import { archivePlanGroup, createPlanGroup, listPlanGroups, setPlanItemGroup, assignedSubjectId, unassignTransaction, type PlanningPeriod } from "./api";
import { GroupAssignDialog } from "./GroupAssignDialog";
import { GroupCreateItemDialog } from "./GroupCreateItemDialog";
import { EmptyState } from "../../shared/ui/EmptyState";

interface PlanGroupsPanelProps {
  bookID: string;
  baseCurrency: Currency;
  // Every plan item of the period, so an ungrouped one can be filed here.
  items: PlanItem[];
  period: PlanningPeriod;
  categories: Category[];
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
}

// A group composes plan items. It never holds an amount of its own, so its
// planned figure is always the sum of its children, and its actual is shown in
// two parts that are never mixed: what its items received, and what was
// assigned to the composite because the split was unknown.
export function PlanGroupsPanel({ bookID, baseCurrency, items, period, categories, accounts, onNotify }: PlanGroupsPanelProps) {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["plan-groups"] });
    await queryClient.invalidateQueries({ queryKey: ["planning"] });
  };
  const groups = useQuery({
    queryKey: ["plan-groups", bookID],
    queryFn: () => listPlanGroups(bookID),
    enabled: bookID !== "",
  });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => createPlanGroup(bookID, { name: name.trim() }),
    onSuccess: async (result) => {
      setName("");
      setAdding(false);
      await refresh();
      onNotify(`Group "${result.planGroup.name}" created. Add plan items to it; the group itself holds no amount.`);
    },
  });

  const views = groups.data?.groups ?? [];
  return (
    <section className="panel planning-panel" aria-labelledby="plan-groups-title">
      <div className="panel-heading">
        <div><p className="eyebrow">Composition</p><h2 id="plan-groups-title">Plan groups</h2></div>
        <button className="primary-button compact" type="button" onClick={() => setAdding(true)}><span>Add group</span><span aria-hidden="true">＋</span></button>
      </div>
      <p className="panel-note">
        A group is a composition of plan items, such as a relocation or a renovation. It carries no amount of its own:
        what it plans is the sum of its items. A transaction assigned to the group belongs to the whole composite and
        is never spread over the items, because nobody said which of them it paid for.
      </p>

      {groups.isError ? <p className="form-error" role="alert">{humanError(groups.error)}</p> : null}
      {views.length === 0 && !groups.isPending ? <EmptyState>No groups yet. A group gathers plan items that belong to one thing — a trip, a move — so they can be tracked together.</EmptyState> : null}

      <ul className="plan-list">
        {views.map((view) => (
          <GroupRow key={view.group.id} bookID={bookID} view={view} items={items} baseCurrency={baseCurrency}
            period={period} categories={categories} accounts={accounts} onNotify={onNotify} onChanged={refresh} />
        ))}
      </ul>

      <Modal
        open={adding} onClose={() => setAdding(false)} title="New plan group"
        subtitle="Name the composite. Its planned value comes from the items you put in it."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form="plan-group-form" disabled={create.isPending}>
            <span>{create.isPending ? "Creating…" : "Create group"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="plan-group-form" className="stacked-form"
          onSubmit={(event) => { event.preventDefault(); if (name.trim()) create.mutate(); }}>
          <label>Name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Vietnam relocation" required autoFocus /></label>
          <p className="panel-note">Base currency of this book is {baseCurrency}; items inside a group may be in any currency.</p>
          {create.error ? <p className="form-error" role="alert">{humanError(create.error)}</p> : null}
        </form>
      </Modal>
    </section>
  );
}

interface GroupRowProps {
  bookID: string;
  view: PlanGroupView;
  items: PlanItem[];
  baseCurrency: Currency;
  period: PlanningPeriod;
  categories: Category[];
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

function GroupRow({ bookID, view, items, baseCurrency, period, categories, accounts, onNotify, onChanged }: GroupRowProps) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [itemID, setItemID] = useState("");
  const group = view.group;

  const attach = useMutation({
    mutationFn: () => setPlanItemGroup(bookID, itemID, group.id),
    onSuccess: async () => { setItemID(""); setAdding(false); await onChanged(); onNotify(`Added to "${group.name}".`); },
  });
  const detach = useMutation({
    mutationFn: (id: string) => setPlanItemGroup(bookID, id, ""),
    onSuccess: async () => { await onChanged(); onNotify("Removed from the group. Its transactions are unchanged."); },
  });
  const archive = useMutation({
    mutationFn: () => archivePlanGroup(bookID, group.id, group.archivedAt == null),
    onSuccess: async () => { await onChanged(); onNotify(group.archivedAt == null ? `"${group.name}" archived.` : `"${group.name}" restored.`); },
  });
  const unassign = useMutation({
    mutationFn: (transactionID: string) => unassignTransaction(bookID, transactionID),
    onSuccess: async () => { await onChanged(); onNotify("The transaction no longer belongs to this group."); },
  });

  const free = items.filter((item) => !item.groupId && !item.cancelledAt);
  const incomplete = view.aggregationStatus === "incomplete";

  return (
    <li className={`plan-row ${group.archivedAt ? "cancelled" : ""}`}>
      <div className="plan-row-head">
        <div>
          <strong>{group.name}</strong>
          <small>{view.children.length} {view.children.length === 1 ? "item" : "items"}{view.currencies.length > 1 ? ` · ${view.currencies.join(", ")}` : ""}</small>
        </div>
        <span className="plan-row-status">
          {incomplete ? <span className="status-pill status-overdue" title="A planning rate is missing, so no total is published rather than one being guessed">Rates missing</span> : null}
          <RowMenu label={`Actions for ${group.name}`}>
            <button className="row-menu-item" type="button" onClick={() => setAdding(true)}>Add an existing plan item…</button>
            <button className="row-menu-item" type="button" onClick={() => setCreating(true)}>Create a plan item…</button>
            <button className="row-menu-item" type="button" onClick={() => setAssigning(true)}>Assign a transaction…</button>
            <button className="row-menu-item danger" type="button" disabled={archive.isPending} onClick={() => archive.mutate()}>
              {group.archivedAt ? "Restore this group" : "Archive this group"}
            </button>
          </RowMenu>
        </span>
      </div>

      <div className="plan-row-amounts">
        <div><span>Planned, from items</span><strong>{money(view.plannedBase.minor, view.plannedBase.currency)}</strong>
          {view.plannedByCurrency.length > 1 ? <small>{view.plannedByCurrency.map((value) => money(value.minor, value.currency)).join(" · ")}</small> : null}</div>
        <div><span>Actual on items</span><strong>{money(view.childActualBase.minor, view.childActualBase.currency)}</strong></div>
        <div><span>Actual on the group</span><strong>{money(view.directActualBase.minor, view.directActualBase.currency)}</strong>
          <small>not spread over the items</small></div>
        <div><span>Total actual</span><strong>{money(view.totalActualBase.minor, view.totalActualBase.currency)}</strong>
          {view.totalActualReporting ? <small>{money(view.totalActualReporting.minor, view.totalActualReporting.currency)}</small> : null}</div>
      </div>

      {view.children.length > 0 ? (
        <ul className="allocation-list">
          {view.children.map((child) => (
            <li key={child.item.id}>
              <span>{child.item.name}<small>planned {money(child.item.amount.minor, child.item.amount.currency)} · actual {money(child.allocated.minor, child.allocated.currency)}</small></span>
              <strong>{child.status.replace("_", " ")}</strong>
              <button className="chip" type="button" disabled={detach.isPending} onClick={() => detach.mutate(child.item.id)}
                title="Take this item out of the group; its transactions stay with the item">Remove</button>
            </li>
          ))}
        </ul>
      ) : null}

      {view.directAssignments.length > 0 ? (
        <>
          <p className="panel-note direct-label">
            Assigned directly to group — these belong to the composite as a whole and fulfil none of the plan items above.
          </p>
          <ul className="allocation-list">
            {view.directAssignments.map((assignment) => (
              <li key={assignedSubjectId(assignment)}>
                <span>{payeeName(assignment.description || "Transaction")}<small>{new Date(assignment.effectiveAt).toLocaleDateString("en", { day: "numeric", month: "short" })}</small></span>
                <strong>{money(assignment.bookValue.minor, assignment.bookValue.currency)}</strong>
                <button className="chip danger" type="button" disabled={unassign.isPending} onClick={() => unassign.mutate(assignedSubjectId(assignment))}>Unassign</button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <GroupCreateItemDialog
        open={creating} onClose={() => setCreating(false)} bookID={bookID} group={group}
        baseCurrency={baseCurrency} period={period} categories={categories}
        onNotify={onNotify} onChanged={onChanged}
      />
      <GroupAssignDialog
        open={assigning} onClose={() => setAssigning(false)} bookID={bookID} group={group}
        accounts={accounts} onNotify={onNotify} onChanged={onChanged}
      />

      <Modal
        open={adding} onClose={() => setAdding(false)} title="Add an existing plan item" subtitle={group.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="button" disabled={attach.isPending || itemID === ""} onClick={() => attach.mutate()}>
            <span>{attach.isPending ? "Adding…" : "Add to group"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <div className="field">
          <span className="field-label" id={`group-item-${group.id}`}>Plan item</span>
          <Select labelledBy={`group-item-${group.id}`} value={itemID} onChange={setItemID}
            placeholder={free.length === 0 ? "Every plan item is already in a group" : "Choose a plan item"}
            options={free.map((item) => ({ value: item.id, label: item.name, hint: money(item.amount.minor, item.amount.currency) }))} />
        </div>
        <p className="panel-note">An item belongs to one group at most. Moving it here leaves the transactions assigned to it exactly where they are.</p>
        {attach.error ? <p className="form-error" role="alert">{humanError(attach.error)}</p> : null}
      </Modal>
    </li>
  );
}
